'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { Settings } from 'lucide-react';
import { useParams } from 'next/navigation';
import { Popover, styled } from '@mui/material';
import { useTheme } from 'next-themes';

const StyledPopover = styled(Popover)(() => ({
  '& .MuiPaper-root': {
    backgroundColor: 'var(--popover-bg, #ffffff)',
    borderRadius: '12px',
    marginTop: '10px',
    // minWidth: 270,
    color: 'var(--popover-text, #000000)',
    boxShadow: '0px 10px 38px -10px rgba(0, 0, 0, 0.5), 0px 10px 20px -15px rgba(0, 0, 0, 0.2)',
    border: '1px solid var(--popover-border, #e5e5e5)',
    '& .MuiList-root': {
      padding: '8px',
    },
    '& .MuiMenuItem-root': {
      fontSize: '14px',
      padding: '12px 16px',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: 'var(--popover-text, #000000)',
      transition: 'all 0.2s',
      '&:hover': {
        backgroundColor: 'var(--popover-hover, #f5f5f5)',
      },
      '& .MuiSvgIcon-root': {
        fontSize: 20,
        color: 'var(--popover-text, #000000)',
      },
    },
    '& .MuiDivider-root': {
      margin: '8px 0',
      backgroundColor: 'var(--popover-divider, #e5e5e5)',
    },
  },
}));

export default function WorkspaceSetting() {
  const params = useParams();
  const { theme } = useTheme();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);

  // 设置 CSS 变量用于主题
  const popoverStyle = {
    '--popover-bg': theme === 'dark' ? '#1a1a1a' : '#ffffff',
    '--popover-text': theme === 'dark' ? '#ffffff' : '#000000',
    '--popover-hover': theme === 'dark' ? '#2a2a2a' : '#f5f5f5',
    '--popover-divider': theme === 'dark' ? '#333333' : '#e5e5e5',
    '--popover-border': theme === 'dark' ? '#333333' : '#e5e5e5',
  } as React.CSSProperties;

  const menuItems = useMemo(() => {
    return (
      <div className="flex flex-col" onClick={(e) => e.stopPropagation()}>
        {['Members', 'Settings'].map((title) => (
          <Link
            key={title}
            href={`/workspace/${params.workspaceId}/${title.toLowerCase()}`}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-[#141414] text-text-primary transition-colors"
          >
            <span className="text-sm">{title}</span>
          </Link>
        ))}
      </div>
    );
  }, [params.workspaceId]);

  return (
    <div>
      <button
        onClick={handleClick}
        className="hover:bg-[#E9E9E9] dark:hover:bg-[#202020] box-border content-stretch flex gap-2 h-8 items-center justify-center px-1.5 py-0 relative rounded-[8px] shrink-0 transition-colors cursor-pointer"
      >
        <Settings size={20} stroke={theme === 'dark' ? 'white' : 'black'} />
      </button>
      <StyledPopover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={popoverStyle}
      >
        <div className="p-1 max-h-[400px] overflow-y-auto text-xs!">{menuItems}</div>
      </StyledPopover>
    </div>
  );
}
